import React, { useContext, useState, useEffect } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { menuPermissionOptions } from "../../layout/MainListItems";
import { applyBranding, resolveBrandingUrl } from "../../utils/branding";
import phoneCountries, {
	defaultPhoneCountryCode,
	formatPhoneCountry
} from "../../utils/phoneCountries";

const defaultBrandingSettings = {
	siteTitle: "CRM Multiagente",
	siteWidgetTitle: "Interno",
	siteWidgetPoweredName: "Kodelr",
	siteWidgetPoweredUrl: "https://kodelr.com",
	landingEmbedUrl: "",
	loginHeadline: "Centraliza tus conversaciones y convierte mas rapido",
	loginSubtitle: "Gestiona WhatsApp, equipos, seguimientos y automatizaciones desde un solo espacio.",
	loginPrimaryLinkLabel: "Solicitar demo",
	loginPrimaryLinkUrl: "",
	loginSecondaryLinkLabel: "Soporte",
	loginSecondaryLinkUrl: "",
	loginLinks: JSON.stringify([
		{ label: "Solicitar demo", url: "" },
		{ label: "Soporte", url: "" },
	]),
	loginSlides: JSON.stringify(
		[
			{
				imageUrl: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80",
				title: "Atencion organizada",
				description: "Conserva cada conversacion, responsable y seguimiento en contexto.",
			},
			{
				imageUrl: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1400&q=80",
				title: "Equipos conectados",
				description: "Administra conversaciones, areas y automatizaciones sin perder visibilidad.",
			},
			{
				imageUrl: "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80",
				title: "Seguimiento claro",
				description: "Haz que cada oportunidad avance con recordatorios y secuencias.",
			},
		],
		null,
		2
	),
};

const getSettingFromList = (settings, key) =>
	settings.find(setting => setting.key === key)?.value;

const getBrandingSnapshot = settings =>
	Object.fromEntries(
		Object.entries(defaultBrandingSettings).map(([key, value]) => [
			key,
			getSettingFromList(settings, key) ?? value,
		])
	);

const parseEditableJsonList = (value, fallback, normalize) => {
	try {
		const parsed = JSON.parse(value || "[]");
		if (!Array.isArray(parsed)) return fallback;
		return parsed.map(normalize);
	} catch {
		return fallback;
	}
};

const metaSettingKeys = [
	"facebookAppId",
	"facebookAppSecret",
	"facebookVerifyToken",
	"instagramAppId",
	"instagramAppSecret",
	"instagramVerifyToken",
];

const smtpSettingKeys = [
	"smtpEnabled",
	"smtpHost",
	"smtpPort",
	"smtpSecure",
	"smtpUser",
	"smtpPass",
	"smtpFromName",
	"smtpFromEmail",
	"smtpNotificationRecipients",
	"smtpNotifyLoginEnabled",
];

const emptyManualAccount = {
	companyName: "",
	name: "",
	email: "",
	password: "",
	contactPhoneCountryCode: defaultPhoneCountryCode,
	contactPhoneNumber: "",
};

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "flex-start",
		padding: theme.spacing(8, 3, 3),
		[theme.breakpoints.down("sm")]: {
			padding: theme.spacing(7, 1, 2),
		},
	},
	container: {
		width: "100%",
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},
	paperColumn: {
		padding: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		marginBottom: 12,
	},
	tabsPaper: {
		marginBottom: 12,
	},
	tabContent: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: theme.spacing(1.5),
	},
	buttonGroup: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		width: "100%",
	},
	inlineActions: {
		display: "flex",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		flexWrap: "wrap",
	},
	brandingGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
		gap: theme.spacing(2),
		width: "100%",
	},
	brandingItem: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
	},
	brandingPreview: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		height: 86,
		border: "1px solid rgba(0, 0, 0, 0.12)",
		borderRadius: 6,
		background: "#fff",
		overflow: "hidden",
	},
	brandingLogo: {
		maxWidth: "100%",
		maxHeight: 58,
		objectFit: "contain",
	},
	brandingFavicon: {
		width: 42,
		height: 42,
		objectFit: "contain",
	},
	fileName: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		color: theme.palette.text.secondary,
	},
	logBox: {
		marginTop: theme.spacing(1),
		"& textarea": {
			fontFamily: '"Inter", sans-serif',
			fontSize: 12,
			lineHeight: 1.45,
		},
	},
	menuPermissions: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
		gap: theme.spacing(0.5, 1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		width: "100%",
	},
	settingsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
		gap: theme.spacing(1),
		width: "100%",
	},
	repeaterList: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
	},
	repeaterItem: {
		border: "1px solid rgba(0, 0, 0, 0.12)",
		borderRadius: 6,
		padding: theme.spacing(1.5),
		background: "#fff",
	},
	repeaterHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

}));

const Settings = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isSuperAdmin = user?.profile === "superadmin";

	const [settings, setSettings] = useState([]);
	const [cloudLogs, setCloudLogs] = useState("");
	const [cloudLogsDate, setCloudLogsDate] = useState("");
	const [loadingCloudLogs, setLoadingCloudLogs] = useState(false);
	const [logoFile, setLogoFile] = useState(null);
	const [faviconFile, setFaviconFile] = useState(null);
	const [savingBranding, setSavingBranding] = useState(false);
	const [savedBranding, setSavedBranding] = useState(defaultBrandingSettings);
	const [testingSmtp, setTestingSmtp] = useState(false);
	const [smtpTestEmail, setSmtpTestEmail] = useState("");
	const [activeTab, setActiveTab] = useState("general");
	const [creatingAccount, setCreatingAccount] = useState(false);
	const [manualAccount, setManualAccount] = useState(emptyManualAccount);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
				setSavedBranding(getBrandingSnapshot(data));
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex].value = data.setting.value;
					} else {
						aux.push(data.setting);
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveSetting = async settingKey => {
		try {
			await api.put(`/settings/${settingKey}`, {
				value: getSettingValue(settingKey),
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveOpenAiSettings = async () => {
		try {
			const requests = [
				api.put("/settings/openAiApiKey", {
					value: getSettingValue("openAiApiKey"),
				})
			];

			if (isSuperAdmin) {
				requests.push(api.put("/settings/newAiConfigsUseOwnApiKey", {
					value: getSettingValue("newAiConfigsUseOwnApiKey") || "disabled",
				}));
			}

			await Promise.all(requests);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveDashboardWebChatSettings = async () => {
		try {
			await Promise.all([
				api.put("/settings/dashboardWebChatScript", {
					value: getSettingValue("dashboardWebChatScript"),
				}),
				api.put("/settings/dashboardWebChatPages", {
					value: getSettingValue("dashboardWebChatPages"),
				}),
				api.put("/settings/dashboardWebChatLauncherMode", {
					value: getSettingValue("dashboardWebChatLauncherMode") || "bubble",
				}),
			]);
			window.dispatchEvent(new CustomEvent("dashboardWebChatSettingsChanged"));
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveExpirationBadgeLinks = async () => {
		const links = getExpirationBadgeLinksValue()
			.map(link => ({
				label: String(link.label || "").trim(),
				url: String(link.url || "").trim(),
			}))
			.filter(link => link.label && link.url);

		try {
			await api.put("/settings/expirationBadgeLinks", {
				value: JSON.stringify(links),
			});
			window.dispatchEvent(
				new CustomEvent("expirationBadgeLinkChanged", {
					detail: {
						links,
					},
				})
			);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveSidepanelExtensionSettings = async () => {
		try {
			const installUrl = getSettingValue("sidepanelExtensionInstallUrl").trim();
			const headerButtonEnabled =
				getSettingValue("sidepanelAppsHeaderButtonEnabled") !== "disabled";

			await Promise.all([
				api.put("/settings/sidepanelExtensionInstallUrl", {
					value: installUrl,
				}),
				api.put("/settings/sidepanelAppsHeaderButtonEnabled", {
					value: headerButtonEnabled ? "enabled" : "disabled",
				}),
			]);
			window.dispatchEvent(
				new CustomEvent("sidepanelExtensionInstallUrlChanged", {
					detail: {
						value: installUrl,
						headerButtonEnabled,
					},
				})
			);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveMetaSettings = async () => {
		try {
			await Promise.all(
				metaSettingKeys.map(settingKey =>
					api.put(`/settings/${settingKey}`, {
						value: getSettingValue(settingKey),
					})
				)
			);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSaveSmtpSettings = async () => {
		try {
			await Promise.all(
				smtpSettingKeys.map(settingKey =>
					api.put(`/settings/${settingKey}`, {
						value: getSmtpSettingValue(settingKey),
					})
				)
			);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleSendSmtpTest = async () => {
		setTestingSmtp(true);
		try {
			await api.post("/settings/smtp/test", {
				to: smtpTestEmail || undefined,
			});
			toast.success("Correo de prueba enviado.");
		} catch (err) {
			toastError(err);
		} finally {
			setTestingSmtp(false);
		}
	};

	const handleManualAccountChange = event => {
		const { name, value } = event.target;
		setManualAccount(prevState => ({
			...prevState,
			[name]: name === "contactPhoneNumber" ? value.replace(/\D/g, "") : value,
		}));
	};

	const handleCreateManualAccount = async event => {
		event.preventDefault();

		if (
			!manualAccount.name.trim() ||
			!manualAccount.email.trim() ||
			manualAccount.password.length < 5 ||
			!manualAccount.contactPhoneNumber.trim()
		) {
			toast.error("Completa los datos requeridos. La contraseña debe tener al menos 5 caracteres.");
			return;
		}

		setCreatingAccount(true);
		try {
			await api.post("/superadmin/accounts", {
				...manualAccount,
				email: manualAccount.email.trim(),
				name: manualAccount.name.trim(),
				companyName: manualAccount.companyName.trim(),
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
			});
			setManualAccount(emptyManualAccount);
			toast.success("Cuenta creada correctamente.");
		} catch (err) {
			toastError(err);
		} finally {
			setCreatingAccount(false);
		}
	};

	const handleSaveBrandingSettings = async () => {
		if (!hasBrandingChanges()) return;

		const formData = new FormData();
		if (logoFile) formData.append("logo", logoFile);
		if (faviconFile) formData.append("favicon", faviconFile);
		Object.keys(defaultBrandingSettings).forEach(key => {
			formData.append(key, getBrandingSettingValue(key));
		});

		setSavingBranding(true);
		try {
			const { data } = await api.put("/settings/branding", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			applyBranding(data);
			setSettings(prevState => {
				const nextValues = {
					siteLogoUrl: data.logoUrl,
					siteFaviconUrl: data.faviconUrl,
					siteTitle: data.siteTitle,
					siteWidgetTitle: data.widgetTitle,
					siteWidgetPoweredName: data.widgetPoweredName,
					siteWidgetPoweredUrl: data.widgetPoweredUrl,
					landingEmbedUrl: data.landingEmbedUrl,
					loginHeadline: data.loginHeadline,
					loginSubtitle: data.loginSubtitle,
					loginPrimaryLinkLabel: data.loginPrimaryLinkLabel,
					loginPrimaryLinkUrl: data.loginPrimaryLinkUrl,
					loginSecondaryLinkLabel: data.loginSecondaryLinkLabel,
					loginSecondaryLinkUrl: data.loginSecondaryLinkUrl,
					loginLinks: data.loginLinks,
					loginSlides: data.loginSlides,
				};
				const nextSettings = [...prevState];

				Object.entries(nextValues).forEach(([key, value]) => {
					const index = nextSettings.findIndex(setting => setting.key === key);
					if (index >= 0) {
						nextSettings[index] = { ...nextSettings[index], value };
					} else {
						nextSettings.push({ key, value });
					}
				});

				return nextSettings;
			});
			setSavedBranding({
				...defaultBrandingSettings,
				siteTitle: data.siteTitle,
				siteWidgetTitle: data.widgetTitle,
				siteWidgetPoweredName: data.widgetPoweredName,
				siteWidgetPoweredUrl: data.widgetPoweredUrl,
				landingEmbedUrl: data.landingEmbedUrl,
				loginHeadline: data.loginHeadline,
				loginSubtitle: data.loginSubtitle,
				loginPrimaryLinkLabel: data.loginPrimaryLinkLabel,
				loginPrimaryLinkUrl: data.loginPrimaryLinkUrl,
				loginSecondaryLinkLabel: data.loginSecondaryLinkLabel,
				loginSecondaryLinkUrl: data.loginSecondaryLinkUrl,
				loginLinks: data.loginLinks,
				loginSlides: data.loginSlides,
			});
			setLogoFile(null);
			setFaviconFile(null);
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		} finally {
			setSavingBranding(false);
		}
	};

	const handleLocalSettingChange = e => {
		const { name, value } = e.target;

		setSettings(prevState => {
			const hasSetting = prevState.some(setting => setting.key === name);
			if (!hasSetting) return [...prevState, { key: name, value }];

			return prevState.map(setting =>
				setting.key === name ? { ...setting, value } : setting
			);
		});
	};

	const handleLocalBooleanSettingChange = e => {
		const { name, checked } = e.target;
		const value = checked ? "enabled" : "disabled";

		setSettings(prevState => {
			const hasSetting = prevState.some(setting => setting.key === name);
			if (!hasSetting) return [...prevState, { key: name, value }];

			return prevState.map(setting =>
				setting.key === name ? { ...setting, value } : setting
			);
		});
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting?.value || "";
	};

	const getSmtpSettingValue = key => {
		if (key === "smtpPort") return getSettingValue(key) || "465";
		if (key === "smtpFromName") return getSettingValue(key) || "CRM Multiagente";
		return getSettingValue(key);
	};

	const isSettingEnabled = key => getSettingValue(key) === "enabled";

	const getBrandingSettingValue = key =>
		getSettingFromList(settings, key) ?? defaultBrandingSettings[key] ?? "";

	const getLoginLinksValue = () =>
		parseEditableJsonList(
			getBrandingSettingValue("loginLinks"),
			[
				{
					label: getBrandingSettingValue("loginPrimaryLinkLabel"),
					url: getBrandingSettingValue("loginPrimaryLinkUrl"),
				},
				{
					label: getBrandingSettingValue("loginSecondaryLinkLabel"),
					url: getBrandingSettingValue("loginSecondaryLinkUrl"),
				},
			],
			link => ({
				label: String(link?.label || ""),
				url: String(link?.url || ""),
			})
		);

	const getLoginSlidesValue = () =>
		parseEditableJsonList(
			getBrandingSettingValue("loginSlides"),
			JSON.parse(defaultBrandingSettings.loginSlides),
			slide => ({
				imageUrl: String(slide?.imageUrl || ""),
				title: String(slide?.title || ""),
				description: String(slide?.description || ""),
			})
		);

	const getExpirationBadgeLinksValue = () => {
		return parseEditableJsonList(
			getSettingValue("expirationBadgeLinks"),
			[{ label: "Administrar suscripcion", url: "" }],
			link => ({
				label: String(link?.label || ""),
				url: String(link?.url || ""),
			})
		);
	};

	const setJsonSettingValue = (key, items) => {
		setSettings(prevState => {
			const value = JSON.stringify(items, null, 2);
			const hasSetting = prevState.some(setting => setting.key === key);
			if (!hasSetting) return [...prevState, { key, value }];

			return prevState.map(setting =>
				setting.key === key ? { ...setting, value } : setting
			);
		});
	};

	const updateLoginLink = (index, field, value) => {
		const links = getLoginLinksValue();
		const nextLinks = links.map((link, itemIndex) =>
			itemIndex === index ? { ...link, [field]: value } : link
		);
		setJsonSettingValue("loginLinks", nextLinks);
	};

	const addLoginLink = () => {
		setJsonSettingValue("loginLinks", [
			...getLoginLinksValue(),
			{ label: "", url: "" },
		]);
	};

	const removeLoginLink = index => {
		const nextLinks = getLoginLinksValue().filter((_, itemIndex) => itemIndex !== index);
		setJsonSettingValue("loginLinks", nextLinks);
	};

	const updateLoginSlide = (index, field, value) => {
		const slides = getLoginSlidesValue();
		const nextSlides = slides.map((slide, itemIndex) =>
			itemIndex === index ? { ...slide, [field]: value } : slide
		);
		setJsonSettingValue("loginSlides", nextSlides);
	};

	const addLoginSlide = () => {
		setJsonSettingValue("loginSlides", [
			...getLoginSlidesValue(),
			{ imageUrl: "", title: "", description: "" },
		]);
	};

	const removeLoginSlide = index => {
		const nextSlides = getLoginSlidesValue().filter((_, itemIndex) => itemIndex !== index);
		setJsonSettingValue("loginSlides", nextSlides);
	};

	const updateExpirationBadgeLink = (index, field, value) => {
		const links = getExpirationBadgeLinksValue();
		const nextLinks = links.map((link, itemIndex) =>
			itemIndex === index ? { ...link, [field]: value } : link
		);
		setJsonSettingValue("expirationBadgeLinks", nextLinks);
	};

	const addExpirationBadgeLink = () => {
		setJsonSettingValue("expirationBadgeLinks", [
			...getExpirationBadgeLinksValue(),
			{ label: "", url: "" },
		]);
	};

	const removeExpirationBadgeLink = index => {
		const nextLinks = getExpirationBadgeLinksValue().filter(
			(_, itemIndex) => itemIndex !== index
		);
		setJsonSettingValue(
			"expirationBadgeLinks",
			nextLinks.length > 0
				? nextLinks
				: [{ label: "Administrar suscripcion", url: "" }]
		);
	};

	const hasBrandingChanges = () =>
		Boolean(logoFile || faviconFile) ||
		Object.keys(defaultBrandingSettings).some(
			key => getBrandingSettingValue(key) !== savedBranding[key]
		);

	const parseDashboardWebChatPages = () => {
		const value = getSettingValue("dashboardWebChatPages");
		if (!value) return ["dashboard"];

		try {
			const pages = JSON.parse(value);
			return Array.isArray(pages) ? pages : ["dashboard"];
		} catch {
			return ["dashboard"];
		}
	};

	const toggleDashboardWebChatPage = page => {
		const pages = parseDashboardWebChatPages();
		const nextPages = pages.includes(page)
			? pages.filter(item => item !== page)
			: [...pages, page];

		setSettings(prevState => {
			const hasSetting = prevState.some(setting => setting.key === "dashboardWebChatPages");
			const nextSetting = {
				key: "dashboardWebChatPages",
				value: JSON.stringify(nextPages),
			};

			return hasSetting
				? prevState.map(setting =>
						setting.key === "dashboardWebChatPages" ? { ...setting, ...nextSetting } : setting
				  )
				: [...prevState, nextSetting];
		});
	};

	const loadCloudLogs = async () => {
		setLoadingCloudLogs(true);
		try {
			const { data } = await api.get("/settings/system-logs?limit=300");
			setCloudLogs((data.logs || []).join("\n"));
			setCloudLogsDate(data.generatedAt || new Date().toISOString());
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingCloudLogs(false);
		}
	};

	const copyCloudLogs = async () => {
		try {
			await navigator.clipboard.writeText(cloudLogs);
			toast.success("Logs copiados.");
		} catch {
			toast.error("No se pudieron copiar los logs.");
		}
	};

	const downloadCloudLogs = () => {
		const blob = new Blob([cloudLogs], { type: "text/plain;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `kodelr-cloud-logs-${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="lg">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.tabsPaper} variant="outlined">
					<Tabs
						value={activeTab}
						onChange={(_, value) => setActiveTab(value)}
						indicatorColor="primary"
						textColor="primary"
						variant="scrollable"
						scrollButtons="auto"
					>
						<Tab value="general" label="General" />
						{isSuperAdmin && <Tab value="accounts" label="Crear cuenta" />}
						<Tab value="branding" label="Marca y Login" />
						<Tab value="webchat" label="WebChat" />
						<Tab value="integrations" label="Integraciones" />
						<Tab value="smtp" label="SMTP" />
						<Tab value="logs" label="Logs" />
					</Tabs>
				</Paper>
				<div className={classes.tabContent}>
				{isSuperAdmin && activeTab === "accounts" && (
				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Crear cuenta manual
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Crea una cuenta nueva con su empresa y usuario administrador sin activar el registro publico.
					</Typography>
					<form onSubmit={handleCreateManualAccount}>
						<div className={classes.settingsGrid}>
							<TextField
								label="Nombre de la empresa"
								name="companyName"
								margin="dense"
								variant="outlined"
								fullWidth
								value={manualAccount.companyName}
								onChange={handleManualAccountChange}
								helperText="Opcional. Si lo dejas vacio se usa el nombre del administrador."
							/>
							<TextField
								label="Nombre del administrador"
								name="name"
								margin="dense"
								variant="outlined"
								fullWidth
								required
								value={manualAccount.name}
								onChange={handleManualAccountChange}
							/>
							<TextField
								label="Correo electronico"
								name="email"
								margin="dense"
								variant="outlined"
								fullWidth
								required
								type="email"
								value={manualAccount.email}
								onChange={handleManualAccountChange}
							/>
							<TextField
								label="Contraseña"
								name="password"
								margin="dense"
								variant="outlined"
								fullWidth
								required
								type="password"
								value={manualAccount.password}
								onChange={handleManualAccountChange}
								helperText="Minimo 5 caracteres."
							/>
							<FormControl variant="outlined" margin="dense" fullWidth required>
								<InputLabel id="manual-account-country-label">Pais</InputLabel>
								<Select
									labelId="manual-account-country-label"
									name="contactPhoneCountryCode"
									value={manualAccount.contactPhoneCountryCode}
									onChange={handleManualAccountChange}
									label="Pais"
								>
									{phoneCountries.map(country => (
										<MenuItem
											key={`${country.code}-${country.dialCode}`}
											value={country.dialCode}
										>
											{formatPhoneCountry(country)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								label="Telefono de contacto"
								name="contactPhoneNumber"
								margin="dense"
								variant="outlined"
								fullWidth
								required
								value={manualAccount.contactPhoneNumber}
								onChange={handleManualAccountChange}
								inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
							/>
						</div>
						<div className={classes.inlineActions}>
							<Button
								color="primary"
								variant="contained"
								type="submit"
								disabled={creatingAccount}
							>
								{creatingAccount ? <CircularProgress size={20} /> : "Crear cuenta"}
							</Button>
							<Button
								color="primary"
								variant="outlined"
								type="button"
								onClick={() => setManualAccount(emptyManualAccount)}
								disabled={creatingAccount}
							>
								Limpiar
							</Button>
						</div>
					</form>
				</Paper>
				)}
				{activeTab === "branding" && (
				<>
				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Marca del sitio
					</Typography>
					<div className={classes.brandingGrid}>
						<div className={classes.brandingItem}>
							<Typography variant="body2">Logo</Typography>
							<div className={classes.brandingPreview}>
								<img
									src={
										logoFile
											? URL.createObjectURL(logoFile)
											: resolveBrandingUrl(getSettingValue("siteLogoUrl")) || "/logo.png"
									}
									alt="Logo"
									className={classes.brandingLogo}
								/>
							</div>
							<input
								accept="image/*"
								id="site-logo-upload"
								type="file"
								style={{ display: "none" }}
								onChange={event => setLogoFile(event.target.files?.[0] || null)}
							/>
							<label htmlFor="site-logo-upload">
								<Button variant="outlined" component="span" fullWidth>
									Seleccionar logo
								</Button>
							</label>
							<Typography variant="caption" className={classes.fileName}>
								{logoFile?.name || getSettingValue("siteLogoUrl") || "logo.png"}
							</Typography>
						</div>
						<div className={classes.brandingItem}>
							<Typography variant="body2">Favicon</Typography>
							<div className={classes.brandingPreview}>
								<img
									src={
										faviconFile
											? URL.createObjectURL(faviconFile)
											: resolveBrandingUrl(getSettingValue("siteFaviconUrl")) || "/favicon.ico"
									}
									alt="Favicon"
									className={classes.brandingFavicon}
								/>
							</div>
							<input
								accept="image/*,.ico"
								id="site-favicon-upload"
								type="file"
								style={{ display: "none" }}
								onChange={event => setFaviconFile(event.target.files?.[0] || null)}
							/>
							<label htmlFor="site-favicon-upload">
								<Button variant="outlined" component="span" fullWidth>
									Seleccionar favicon
								</Button>
							</label>
							<Typography variant="caption" className={classes.fileName}>
								{faviconFile?.name || getSettingValue("siteFaviconUrl") || "favicon.ico"}
							</Typography>
						</div>
					</div>
					<TextField
						label="Titulo de la web"
						name="siteTitle"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getBrandingSettingValue("siteTitle")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="URL de landing embebida"
						name="landingEmbedUrl"
						margin="dense"
						variant="outlined"
						fullWidth
						placeholder="https://..."
						value={getBrandingSettingValue("landingEmbedUrl")}
						onChange={handleLocalSettingChange}
						helperText="Si completas esta URL, la ruta principal / mostrara esa landing dentro de un iframe. /login y /signup siguen funcionando aparte."
					/>
					<TextField
						label="Titulo del widget"
						name="siteWidgetTitle"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getBrandingSettingValue("siteWidgetTitle")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Nombre powered by"
						name="siteWidgetPoweredName"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getBrandingSettingValue("siteWidgetPoweredName")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="URL powered by"
						name="siteWidgetPoweredUrl"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getBrandingSettingValue("siteWidgetPoweredUrl")}
						onChange={handleLocalSettingChange}
					/>
					<Typography variant="subtitle1" gutterBottom>
						Login
					</Typography>
					<TextField
						label="Titulo principal del login"
						name="loginHeadline"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getBrandingSettingValue("loginHeadline")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Subtitulo del login"
						name="loginSubtitle"
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						rows={2}
						value={getBrandingSettingValue("loginSubtitle")}
						onChange={handleLocalSettingChange}
					/>
					<Typography variant="subtitle2">
						Links del login
					</Typography>
					<div className={classes.repeaterList}>
						{getLoginLinksValue().map((link, index) => (
							<div className={classes.repeaterItem} key={`login-link-${index}`}>
								<div className={classes.repeaterHeader}>
									<Typography variant="body2">Link {index + 1}</Typography>
									<Button
										color="secondary"
										variant="outlined"
										size="small"
										onClick={() => removeLoginLink(index)}
									>
										Quitar
									</Button>
								</div>
								<div className={classes.settingsGrid}>
									<TextField
										label="Texto"
										margin="dense"
										variant="outlined"
										fullWidth
										value={link.label}
										onChange={event =>
											updateLoginLink(index, "label", event.target.value)
										}
									/>
									<TextField
										label="URL"
										margin="dense"
										variant="outlined"
										fullWidth
										value={link.url}
										onChange={event =>
											updateLoginLink(index, "url", event.target.value)
										}
									/>
								</div>
							</div>
						))}
					</div>
					<Button color="primary" variant="outlined" onClick={addLoginLink}>
						Agregar link
					</Button>
					<Typography variant="subtitle2">
						Slides del login
					</Typography>
					<div className={classes.repeaterList}>
						{getLoginSlidesValue().map((slide, index) => (
							<div className={classes.repeaterItem} key={`login-slide-${index}`}>
								<div className={classes.repeaterHeader}>
									<Typography variant="body2">Slide {index + 1}</Typography>
									<Button
										color="secondary"
										variant="outlined"
										size="small"
										onClick={() => removeLoginSlide(index)}
									>
										Quitar
									</Button>
								</div>
								<TextField
									label="URL de imagen"
									margin="dense"
									variant="outlined"
									fullWidth
									value={slide.imageUrl}
									onChange={event =>
										updateLoginSlide(index, "imageUrl", event.target.value)
									}
								/>
								<div className={classes.settingsGrid}>
									<TextField
										label="Titulo"
										margin="dense"
										variant="outlined"
										fullWidth
										value={slide.title}
										onChange={event =>
											updateLoginSlide(index, "title", event.target.value)
										}
									/>
									<TextField
										label="Descripcion"
										margin="dense"
										variant="outlined"
										fullWidth
										value={slide.description}
										onChange={event =>
											updateLoginSlide(index, "description", event.target.value)
										}
									/>
								</div>
							</div>
						))}
					</div>
					<Button color="primary" variant="outlined" onClick={addLoginSlide}>
						Agregar slide
					</Button>
					<Button
						color="primary"
						variant="contained"
						fullWidth
						onClick={handleSaveBrandingSettings}
						disabled={savingBranding || !hasBrandingChanges()}
					>
						{savingBranding ? <CircularProgress size={20} /> : "Guardar marca"}
					</Button>
				</Paper>
				</>
				)}
				{activeTab === "general" && (
				<>
				<Paper className={classes.paper}>
					<div>
						<Typography variant="body1">
							{i18n.t("settings.settings.userCreation.name")}
						</Typography>
						<FormControlLabel
							control={
								<Checkbox
									name="signupResellerEnabled"
									color="primary"
									checked={isSettingEnabled("signupResellerEnabled")}
									onChange={event =>
										handleChangeSetting({
											target: {
												name: event.target.name,
												value: event.target.checked ? "enabled" : "disabled",
											},
										})
									}
								/>
							}
							label="Reseller habilitado en registros"
						/>
					</div>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						InputProps={{ readOnly: true }}
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Boton de expiracion
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Al hacer clic en el aviso de expiracion se mostrara un desplegable con estos links.
					</Typography>
					<div className={classes.repeaterList}>
						{getExpirationBadgeLinksValue().map((link, index) => (
							<div className={classes.repeaterItem} key={`expiration-link-${index}`}>
								<div className={classes.repeaterHeader}>
									<Typography variant="body2">Link {index + 1}</Typography>
									<Button
										color="secondary"
										variant="outlined"
										size="small"
										onClick={() => removeExpirationBadgeLink(index)}
									>
										Quitar
									</Button>
								</div>
								<div className={classes.settingsGrid}>
									<TextField
										label="Nombre"
										margin="dense"
										variant="outlined"
										fullWidth
										value={link.label}
										onChange={event =>
											updateExpirationBadgeLink(index, "label", event.target.value)
										}
									/>
									<TextField
										label="URL"
										margin="dense"
										variant="outlined"
										fullWidth
										value={link.url}
										onChange={event =>
											updateExpirationBadgeLink(index, "url", event.target.value)
										}
									/>
								</div>
							</div>
						))}
					</div>
					<Button
						color="primary"
						variant="outlined"
						onClick={addExpirationBadgeLink}
					>
						Agregar link
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleSaveExpirationBadgeLinks}
					>
						Guardar links
					</Button>
				</Paper>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Extension de sidepanel
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								name="sidepanelAppsHeaderButtonEnabled"
								checked={
									getSettingValue("sidepanelAppsHeaderButtonEnabled") !==
									"disabled"
								}
								onChange={handleLocalBooleanSettingChange}
							/>
						}
						label="Mostrar boton Apps en el encabezado"
					/>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Esta URL se mostrara cuando el usuario intente abrir Apps sin tener instalada la extension.
					</Typography>
					<TextField
						id="sidepanel-extension-install-url-setting"
						label="URL para instalar la extension"
						name="sidepanelExtensionInstallUrl"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("sidepanelExtensionInstallUrl")}
						onChange={handleLocalSettingChange}
					/>
					<Button
						color="primary"
						variant="contained"
						onClick={handleSaveSidepanelExtensionSettings}
					>
						Guardar sidepanel
					</Button>
				</Paper>
				</>
				)}
				{activeTab === "webchat" && (
				<>

				<Paper className={classes.paperColumn}>
					<TextField
						id="dashboard-webchat-script-setting"
						label={i18n.t("settings.settings.dashboardWebChatScript.name")}
						name="dashboardWebChatScript"
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						rows={6}
						value={settings && settings.length > 0 && getSettingValue("dashboardWebChatScript")}
						onChange={handleLocalSettingChange}
						helperText={i18n.t("settings.settings.dashboardWebChatScript.helper")}
					/>
					<Typography variant="subtitle2">
						Mostrar WebChat en
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						fullWidth
						name="dashboardWebChatLauncherMode"
						value={getSettingValue("dashboardWebChatLauncherMode") || "bubble"}
						onChange={handleLocalSettingChange}
					>
						<option value="bubble">Burbuja flotante</option>
						<option value="header">Boton en encabezado</option>
					</Select>
					<div className={classes.menuPermissions}>
						{menuPermissionOptions.map(option => (
							<FormControlLabel
								key={option.value}
								control={
									<Checkbox
										color="primary"
										checked={parseDashboardWebChatPages().includes(option.value)}
										onChange={() => toggleDashboardWebChatPage(option.value)}
									/>
								}
								label={option.label}
							/>
						))}
					</div>
					<Button
						color="primary"
						variant="contained"
						fullWidth
						onClick={handleSaveDashboardWebChatSettings}
					>
						Guardar
					</Button>
				</Paper>
				</>
				)}
				{activeTab === "integrations" && (
				<>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						OpenAI
					</Typography>
					<TextField
						id="openai-api-key-setting"
						label="OpenAI"
						name="openAiApiKey"
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						value={settings && settings.length > 0 && getSettingValue("openAiApiKey")}
						onChange={handleLocalSettingChange}
					/>
					{isSuperAdmin && (
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									name="newAiConfigsUseOwnApiKey"
									checked={isSettingEnabled("newAiConfigsUseOwnApiKey")}
									onChange={handleLocalBooleanSettingChange}
								/>
							}
							label="Crear cuentas nuevas con API key propia"
						/>
					)}
					<div className={classes.inlineActions}>
						<Button
							color="primary"
							variant="contained"
							onClick={handleSaveOpenAiSettings}
						>
							Guardar
						</Button>
						<Button
							color="primary"
							variant="outlined"
							onClick={() => window.open("/settings/ai-logs", "_blank")}
						>
							Abrir logs
						</Button>
					</div>
				</Paper>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Integraciones Meta
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Configura los datos globales de la app para Messenger e Instagram.
					</Typography>
					<TextField
						label="Facebook App ID"
						name="facebookAppId"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("facebookAppId")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Facebook App Secret"
						name="facebookAppSecret"
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						value={getSettingValue("facebookAppSecret")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Facebook Verify Token"
						name="facebookVerifyToken"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("facebookVerifyToken")}
						onChange={handleLocalSettingChange}
						helperText="Debe coincidir con el token de verificacion configurado en el webhook de Meta."
					/>
					<TextField
						label="Instagram App ID"
						name="instagramAppId"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("instagramAppId")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Instagram App Secret"
						name="instagramAppSecret"
						margin="dense"
						variant="outlined"
						fullWidth
						type="password"
						value={getSettingValue("instagramAppSecret")}
						onChange={handleLocalSettingChange}
					/>
					<TextField
						label="Instagram Verify Token"
						name="instagramVerifyToken"
						margin="dense"
						variant="outlined"
						fullWidth
						value={getSettingValue("instagramVerifyToken")}
						onChange={handleLocalSettingChange}
						helperText="Debe coincidir con el token de verificacion configurado en el webhook de Meta."
					/>
					<Button
						color="primary"
						variant="contained"
						fullWidth
						onClick={handleSaveMetaSettings}
					>
						Guardar Meta
					</Button>
				</Paper>
				</>
				)}
				{activeTab === "smtp" && (
				<>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						SMTP y notificaciones por email
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Configura el servidor SMTP global para enviar correos del sistema.
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								name="smtpEnabled"
								checked={isSettingEnabled("smtpEnabled")}
								onChange={handleLocalBooleanSettingChange}
							/>
						}
						label="Activar envio por SMTP"
					/>
					<div className={classes.settingsGrid}>
						<TextField
							label="Servidor SMTP"
							name="smtpHost"
							margin="dense"
							variant="outlined"
							fullWidth
							value={getSmtpSettingValue("smtpHost")}
							onChange={handleLocalSettingChange}
						/>
						<TextField
							label="Puerto"
							name="smtpPort"
							margin="dense"
							variant="outlined"
							fullWidth
							type="number"
							value={getSmtpSettingValue("smtpPort")}
							onChange={handleLocalSettingChange}
						/>
					</div>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								name="smtpSecure"
								checked={isSettingEnabled("smtpSecure")}
								onChange={handleLocalBooleanSettingChange}
							/>
						}
						label="Usar conexion segura SSL/TLS"
					/>
					<div className={classes.settingsGrid}>
						<TextField
							label="Usuario"
							name="smtpUser"
							margin="dense"
							variant="outlined"
							fullWidth
							value={getSmtpSettingValue("smtpUser")}
							onChange={handleLocalSettingChange}
						/>
						<TextField
							label="Contrasena"
							name="smtpPass"
							margin="dense"
							variant="outlined"
							fullWidth
							type="password"
							value={getSmtpSettingValue("smtpPass")}
							onChange={handleLocalSettingChange}
						/>
					</div>
					<div className={classes.settingsGrid}>
						<TextField
							label="Nombre remitente"
							name="smtpFromName"
							margin="dense"
							variant="outlined"
							fullWidth
							value={getSmtpSettingValue("smtpFromName")}
							onChange={handleLocalSettingChange}
						/>
						<TextField
							label="Correo remitente"
							name="smtpFromEmail"
							margin="dense"
							variant="outlined"
							fullWidth
							value={getSmtpSettingValue("smtpFromEmail")}
							onChange={handleLocalSettingChange}
						/>
					</div>
					<TextField
						label="Destinatarios de notificaciones"
						name="smtpNotificationRecipients"
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						rows={3}
						value={getSmtpSettingValue("smtpNotificationRecipients")}
						onChange={handleLocalSettingChange}
						helperText="Puedes separar correos con coma, punto y coma o salto de linea."
					/>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								name="smtpNotifyLoginEnabled"
								checked={isSettingEnabled("smtpNotifyLoginEnabled")}
								onChange={handleLocalBooleanSettingChange}
							/>
						}
						label="Enviar por email las notificaciones de login"
					/>
					<TextField
						label="Correo para prueba"
						margin="dense"
						variant="outlined"
						fullWidth
						value={smtpTestEmail}
						onChange={event => setSmtpTestEmail(event.target.value)}
						helperText="Si lo dejas vacio, se usa el correo de tu usuario."
					/>
					<div className={classes.inlineActions}>
						<Button
							color="primary"
							variant="contained"
							onClick={handleSaveSmtpSettings}
						>
							Guardar SMTP
						</Button>
						<Button
							color="primary"
							variant="outlined"
							onClick={handleSendSmtpTest}
							disabled={testingSmtp}
						>
							{testingSmtp ? <CircularProgress size={20} /> : "Enviar prueba"}
						</Button>
					</div>
				</Paper>
				</>
				)}
				{activeTab === "logs" && (
				<>

				<Paper className={classes.paperColumn}>
					<Typography variant="subtitle1" gutterBottom>
						Logs de la nube
					</Typography>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Consulta las ultimas lineas generadas por el backend para compartir errores de produccion.
					</Typography>
					<div className={classes.inlineActions}>
						<Button
							color="primary"
							variant="contained"
							onClick={loadCloudLogs}
							disabled={loadingCloudLogs}
						>
							{loadingCloudLogs ? <CircularProgress size={20} /> : "Actualizar logs"}
						</Button>
						<Button
							color="primary"
							variant="outlined"
							onClick={copyCloudLogs}
							disabled={!cloudLogs}
						>
							Copiar
						</Button>
						<Button
							color="primary"
							variant="outlined"
							onClick={downloadCloudLogs}
							disabled={!cloudLogs}
						>
							Descargar TXT
						</Button>
					</div>
					{cloudLogsDate && (
						<Typography variant="caption" color="textSecondary">
							Generado: {new Date(cloudLogsDate).toLocaleString()}
						</Typography>
					)}
					<TextField
						className={classes.logBox}
						label="Logs recientes"
						margin="dense"
						variant="outlined"
						fullWidth
						multiline
						rows={12}
						value={cloudLogs}
						placeholder="Presiona Actualizar logs para cargar las ultimas lineas."
						InputProps={{ readOnly: true }}
					/>
				</Paper>
				</>
				)}
				</div>

			</Container>
		</div>
	);
};

export default Settings;

